import { useMemo } from "react";
import NiceModal from "@ebay/nice-modal-react";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";
import { DIMENSIONS, SCORE_LEVELS } from "../../data/scorecard";
import { KNOWN_AGENTS, KNOWN_GEOS, KNOWN_MANAGERS, KNOWN_TEAMS } from "../../data/mockEvaluations";
import { computeTotals, formatPercent, getMissingItems, suggestLeakage } from "../../utils/scoring";
import ManagerGuideModal from "../ManagerGuideModal/ManagerGuideModal";
import { CardTitle } from "../shared";
import BehavioralFlagsCard from "./BehavioralFlagsCard";
import CallDetailsCard from "./CallDetailsCard";
import CallSummaryCard from "./CallSummaryCard";
import DimensionCard from "./DimensionCard";
import LiveScorePanel from "./LiveScorePanel";

const unique = (values) => [...new Set(values.filter(Boolean))].sort();

function EvaluationForm({ evaluation }) {
    const { draft, log, savedAt, updateInfo, setScore, setNote, setFlag, updateSummary, resetDraft, submit } = evaluation;
    const toast = useToastDispatchContext();

    const totals = useMemo(() => computeTotals(draft.scores), [draft.scores]);
    const missing = useMemo(() => getMissingItems(draft), [draft]);
    const suggestedLeakage = useMemo(() => suggestLeakage(draft.scores), [draft.scores]);
    const flagsRaised = useMemo(
        () => Object.values(draft.flags).filter((flag) => flag.observed === "Yes" || flag.observed === "Partially").length,
        [draft.flags]
    );

    // Names already used in past evaluations (plus the demo ones) feed the autocomplete.
    const suggestions = useMemo(() => ({
        agents: unique([...KNOWN_AGENTS, ...log.map((item) => item.agent)]),
        managers: unique([...KNOWN_MANAGERS, ...log.map((item) => item.manager)]),
        teams: unique([...KNOWN_TEAMS, ...log.map((item) => item.team)]),
        geos: unique([...KNOWN_GEOS, ...log.map((item) => item.geo)]),
    }), [log]);

    const handleSubmit = () => {
        const result = submit();
        if (!result.ok) {
            toast.error("Evaluation incomplete", `Still needed: ${result.missing.join(", ")}`, 5000);
            return;
        }
        toast.success(
            "Evaluation submitted",
            `${result.record.agent} scored ${formatPercent(result.record.salesScore)} — it now counts in the statistics.`,
            5000
        );
        window.scrollTo?.({ top: 0 });
    };

    return (
        <div className="ce-layout">
            <div className="ce-main">
                <CallDetailsCard info={draft.info} onChange={updateInfo} suggestions={suggestions} />

                <section className="ce-card">
                    <CardTitle
                        icon="fas fa-clipboard-check"
                        title="Sales scorecard"
                        hint="Score observable behavior, not personality — click a score again to clear it"
                        right={
                            <div className="ce-legend">
                                {SCORE_LEVELS.map((level) => (
                                    <span key={level.value} className={"ce-legend__item s" + level.value}><b>{level.value}</b> {level.label}</span>
                                ))}
                            </div>
                        }
                    />
                    <div className="ce-dims">
                        {DIMENSIONS.map((dimension) => (
                            <DimensionCard
                                key={dimension.id}
                                dimension={dimension}
                                score={draft.scores[dimension.id]}
                                evidence={draft.evidence[dimension.id]}
                                coaching={draft.coaching[dimension.id]}
                                onScore={setScore}
                                onNote={setNote}
                            />
                        ))}
                    </div>
                </section>

                <BehavioralFlagsCard flags={draft.flags} onChange={setFlag} />
                <CallSummaryCard summary={draft.summary} onChange={updateSummary} suggestedLeakage={suggestedLeakage} />
            </div>

            <LiveScorePanel
                totals={totals}
                flagsRaised={flagsRaised}
                missing={missing}
                savedAt={savedAt}
                onSubmit={handleSubmit}
                onClear={resetDraft}
                onOpenGuide={() => NiceModal.show(ManagerGuideModal)}
            />
        </div>
    );
}

export default EvaluationForm;
